import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// ─── Sheet Stack Manager ───
const sheetStack = [];
let popStateHandlerRegistered = false;

let idCounter = 0;
function nextId() {
  return `sheet-${++idCounter}`;
}

function registerSheet(id, onClose, onBackPress, onDismiss) {
  sheetStack.push({ id, onClose, onBackPress, onDismiss });
  history.pushState({ bottomSheet: id }, '');

  if (!popStateHandlerRegistered) {
    popStateHandlerRegistered = true;
    window.addEventListener('popstate', handleGlobalPopState);
  }
}

function unregisterSheet(id) {
  const idx = sheetStack.findIndex(s => s.id === id);
  if (idx !== -1) sheetStack.splice(idx, 1);

  if (sheetStack.length === 0 && popStateHandlerRegistered) {
    popStateHandlerRegistered = false;
    window.removeEventListener('popstate', handleGlobalPopState);
  }
}

function handleGlobalPopState() {
  if (sheetStack.length === 0) return;
  const top = sheetStack[sheetStack.length - 1];
  if (top.onBackPress) {
    const shouldClose = top.onBackPress();
    if (!shouldClose) {
      // onBackPress handled it internally, re-push state for next back press
      history.pushState({ bottomSheet: top.id }, '');
      return;
    }
  }
  // dismiss 애니메이션 후 닫기 (onDismiss가 있으면 사용)
  if (top.onDismiss) {
    top.onDismiss();
  } else {
    top.onClose();
  }
}

/**
 * Common BottomSheet wrapper component.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onBackPress?: () => boolean  (return true to close, false to handle internally)
 * - expandable?: boolean
 * - minHeight?: string (default '30vh')
 * - maxHeight?: string (default '85vh')
 * - expandedMinHeight?: string (default '85vh')
 * - expandedMaxHeight?: string (default '95vh')
 * - zIndex?: number (default 60)
 * - children: ReactNode
 */
export default function BottomSheet({
  isOpen,
  onClose,
  onBackPress,
  expandable = false,
  minHeight = '30vh',
  maxHeight = '85vh',
  expandedMinHeight = '85vh',
  expandedMaxHeight = '95vh',
  zIndex = 60,
  children,
}) {
  const [entryDone, setEntryDone] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  const handleAreaRef = useRef(null);
  const dragState = useRef({ startY: 0, lastY: 0, lastTime: 0, velocity: 0, isDragging: false, fromHandle: false });
  const sheetIdRef = useRef(null);
  const dismissingRef = useRef(false);

  // Keep refs in sync
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onBackPressRef = useRef(onBackPress);
  onBackPressRef.current = onBackPress;
  const dismissFromBackRef = useRef(null);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setEntryDone(false);
      setExpanded(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Register/unregister sheet in stack
  useEffect(() => {
    if (isOpen) {
      const id = nextId();
      sheetIdRef.current = id;
      registerSheet(
        id,
        () => onCloseRef.current(),
        () => onBackPressRef.current ? onBackPressRef.current() : true,
        () => dismissFromBackRef.current()
      );
    } else {
      if (sheetIdRef.current) {
        unregisterSheet(sheetIdRef.current);
        sheetIdRef.current = null;
      }
    }
    return () => {
      if (sheetIdRef.current) {
        unregisterSheet(sheetIdRef.current);
        sheetIdRef.current = null;
      }
    };
  }, [isOpen]);

  // Animate sheet out, then call callback
  const animateOut = useCallback((cb) => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setDismissing(true);
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (sheet) {
      sheet.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
      sheet.style.transform = 'translateY(100%)';
    }
    if (backdrop) {
      backdrop.style.transition = 'opacity 0.25s ease-out';
      backdrop.style.opacity = '0';
    }
    setTimeout(() => {
      dismissingRef.current = false;
      setDismissing(false);
      cb();
    }, 300);
  }, []);

  // Dismiss via user action (drag, backdrop click) — animate then history.back()
  const dismissSheet = useCallback(() => {
    animateOut(() => history.back());
  }, [animateOut]);

  // Dismiss via back button (popstate) — animate then onClose (history already popped)
  const dismissFromBack = useCallback(() => {
    animateOut(() => onCloseRef.current());
  }, [animateOut]);

  dismissFromBackRef.current = dismissFromBack;

  // Drag handling
  const handleTouchStart = (e) => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const fromHandle = handleAreaRef.current && handleAreaRef.current.contains(e.target);
    const scrollEl = sheet.querySelector('.overflow-y-auto');

    if (!fromHandle && scrollEl && scrollEl.scrollTop > 0) return;

    sheet.style.transition = 'none';
    const y = e.touches[0].clientY;
    dragState.current = { startY: y, lastY: y, lastTime: Date.now(), velocity: 0, isDragging: true, fromHandle };
  };

  const handleTouchMove = (e) => {
    const ds = dragState.current;
    if (!ds.isDragging || !sheetRef.current) return;

    const y = e.touches[0].clientY;
    const now = Date.now();
    const dt = now - ds.lastTime;
    if (dt > 0) {
      ds.velocity = (y - ds.lastY) / dt;
    }
    ds.lastY = y;
    ds.lastTime = now;

    const dy = y - ds.startY;
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;

    if (dy > 0) {
      sheet.style.transform = `translateY(${dy}px)`;
      if (backdrop) {
        backdrop.style.opacity = String(Math.max(0, 1 - dy / 400));
      }
    } else if (ds.fromHandle) {
      if (expandable && !expanded) {
        const upDy = dy * 0.5;
        sheet.style.transform = `translateY(${upDy}px)`;
      } else {
        const upDy = dy * 0.3;
        sheet.style.transform = `translateY(${upDy}px)`;
      }
    } else {
      ds.isDragging = false;
      sheet.style.transition = '';
      sheet.style.transform = '';
    }
  };

  const handleTouchEnd = () => {
    const ds = dragState.current;
    if (!ds.isDragging || !sheetRef.current) return;
    ds.isDragging = false;

    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    const dy = ds.lastY - ds.startY;
    const velocity = ds.velocity;

    sheet.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
    if (backdrop) {
      backdrop.style.transition = 'opacity 0.35s ease-out';
    }

    // Fast flick down or drag > 150px -> dismiss
    if (velocity > 0.5 || dy > 150) {
      dismissSheet();
      return;
    }

    // Expandable: flick up -> expand
    if (expandable && !expanded && (velocity < -0.3 || dy < -80)) {
      setExpanded(true);
      sheet.style.transform = '';
      if (backdrop) backdrop.style.opacity = '1';
      return;
    }

    // Snap back
    sheet.style.transform = '';
    if (backdrop) backdrop.style.opacity = '1';
  };

  if (!isOpen) return null;

  const activeMinHeight = expanded ? expandedMinHeight : minHeight;
  const activeMaxHeight = expanded ? expandedMaxHeight : maxHeight;

  return createPortal(
    <div className={`fixed inset-0 flex items-end justify-center`} style={{ zIndex }}>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-backdrop"
        onClick={dismissSheet}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`relative w-full max-w-lg bg-white rounded-t-2xl flex flex-col ${
          !entryDone ? 'animate-sheet-up' : ''
        } ${expanded ? 'transition-[max-height,min-height] duration-300 ease-out' : ''}`}
        style={{ willChange: 'transform', minHeight: activeMinHeight, maxHeight: activeMaxHeight }}
        onAnimationEnd={() => setEntryDone(true)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Prevent blank space below when dragging up */}
        <div className="absolute left-0 right-0 bg-white" style={{ top: '100%', height: 200 }} />

        {/* Drag Handle */}
        <div ref={handleAreaRef} className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>

        {/* Children */}
        {children}
      </div>
    </div>,
    document.body
  );
}
